import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { DICTIONARIES, type Lang, type TKey } from './dictionary';

const STORAGE_KEY = 'lang';

interface I18nValue {
  lang: Lang;
  /** Přepnutí uživatelem – uloží se i na server. */
  setLang: (lang: Lang) => void;
  /** Nastavení jazyka podle odpovědi serveru (přihlášení, kontext směny). */
  applyServerLang: (lang: Lang) => void;
  t: (key: TKey, vars?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function initialLang(): Lang {
  // localStorage je jen cache, aby první vykreslení nebylo v cizím jazyce.
  // Zdroj pravdy je uživatelský profil na serveru.
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in DICTIONARIES) return stored as Lang;
  return 'cs';
}

interface Props {
  children: ReactNode;
  /** Uloží volbu na uživatele. Chyba se ignoruje – jazyk zůstane aspoň lokálně. */
  persist?: (lang: Lang) => Promise<unknown>;
}

export function I18nProvider({ children, persist }: Props) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    // Skladník se přihlásí na jiném telefonu a musí mít svůj jazyk hned,
    // proto volba patří na uživatele, ne do prohlížeče.
    void persistRef.current?.(next)?.catch?.((err: unknown) => {
      console.warn('[i18n] jazyk se nepodařilo uložit na uživatele:', err);
    });
  }, []);

  const applyServerLang = useCallback((next: Lang) => setLangState(next), []);

  const t = useCallback(
    (key: TKey, vars?: Record<string, string>) => {
      let text: string = DICTIONARIES[lang][key] ?? DICTIONARIES.cs[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) text = text.replace(`{${k}}`, v);
      }
      return text;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLang, applyServerLang, t }),
    [lang, setLang, applyServerLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT musí být uvnitř <I18nProvider>');
  return ctx;
}
